import React, {
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DrawerContext } from '~/providers/DrawerProvider';
import VoidPanel from './AuthorizePanel';
import storage from '~/storage';
import Loading from '../components/Loading';
import ErrorBoundary from '../ErrorBoundary';

const useNeedAuth = () => {
  const { openDrawer } = useContext(DrawerContext);
  return useCallback(
    ({
      entityId,
      permissionName,
      onSubmit,
    }: {
      entityId: string;
      permissionName: string;
      onSubmit: ({
        userId,
        actionToken,
      }: {
        userId: string;
        actionToken: string;
      }) => void;
    }) => {
      openDrawer(({ closeDrawer }) => {
        const handleSubmit = (payload: {
          userId: string;
          actionToken: string;
        }) => {
          onSubmit(payload);
          closeDrawer();
        };
        return (
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>
              <VoidPanel
                entityId={entityId}
                permissionName={permissionName}
                onSubmit={handleSubmit}
              />
            </Suspense>
          </ErrorBoundary>
        );
      });
    },
    []
  );
};

const STORAGE_KEY = 'last_authorizing_users';

export const useUserSuggestion = (
  users: { userId: string; username: string }[]
) => {
  const [userSuggestionIds, _] = useState(() => {
    try {
      const storageValue = storage.get(STORAGE_KEY, []);
      if (!Array.isArray(storageValue)) throw new Error();

      const userIdMap = new Set(users.map(({ userId }) => userId));

      return storageValue.filter((id) => userIdMap.has(id));
    } catch (_) {
      storage.remove(STORAGE_KEY);
      return [];
    }
  });

  const userSuggestions = useMemo(() => {
    const userIdMap = users.reduce((acc, cur) => {
      acc[cur.userId] = cur;
      return acc;
    }, {} as Record<string, typeof users[number]>);
    return userSuggestionIds.map((id) => userIdMap[id]).filter(Boolean);
  }, [userSuggestionIds, users]);

  const saveUserAsSuggestion = useCallback(
    (id: string) => {
      if (userSuggestionIds.includes(id)) return;

      const newArray = [...userSuggestionIds, id].slice(0, 3);
      storage.set(STORAGE_KEY, newArray);
    },
    [userSuggestionIds]
  );

  return {
    userSuggestions,
    saveUserAsSuggestion,
  };
};

export default useNeedAuth;
